import {
  FocusEventHandler,
  InputHTMLAttributes,
  MouseEventHandler,
  useRef,
} from "react";

import { useFormContext } from "react-hook-form";

import { useBooleanState } from "@enkor/toss-slash";

import {
  FormTitleType,
  LoginFormType,
  LoginSchemaType,
} from "@domains/auth/types/authTypes";
import { EyeIcon, EyeOffIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import { MIN_PASSWORD_LENGTH } from "@domains/auth/constants/formLimit";

import * as S from "./FormInputForPassword.style";

interface FormInputForPasswordProps
  extends InputHTMLAttributes<HTMLInputElement> {
  formId: LoginFormType;
  label: FormTitleType;
  hideIndicator?: boolean;
}

const FormInputForPassword = ({
  formId,
  label,
  hideIndicator = false,
  ...rest
}: FormInputForPasswordProps) => {
  const [isShowPw, , hidePwIcon, togglePwIcon] = useBooleanState();
  const [isActive, activateInput] = useBooleanState();

  const passwordIconRef = useRef<HTMLButtonElement>(null);
  const { register, watch, setFocus, formState } =
    useFormContext<LoginSchemaType>();

  const passwordValue = watch("password");

  const handleClickEyeBtn: MouseEventHandler<HTMLButtonElement> = () => {
    togglePwIcon();
    setFocus("password");
  };

  const handleBlurInput: FocusEventHandler<HTMLInputElement> = (event) => {
    if (passwordIconRef.current === event.relatedTarget) return;
    // inactivateInput(); // HACK: inactive 하면 ios safari에서 동작하지 않음.
    hidePwIcon();
  };

  const getErrorStatus = () => {
    const { errors } = formState;

    return {
      isError: !!errors?.[formId] && !!passwordValue.length,
      errorMessage: errors?.[formId]
        ? (errors?.[formId]?.message as string)
        : null,
    };
  };

  const { isError, errorMessage } = getErrorStatus();

  return (
    <S.Container>
      <S.Title>{label}</S.Title>
      <S.InputWrapper $isError={isError}>
        <S.Input
          id={formId}
          placeholder=" "
          type={isShowPw ? "text" : "password"}
          onFocus={activateInput}
          {...register(formId, { onBlur: handleBlurInput })}
          {...rest}
        />
        {isActive && passwordValue && (
          <button
            ref={passwordIconRef}
            type="button"
            onClick={handleClickEyeBtn}
          >
            {isShowPw ? (
              <EyeOffIcon strokeColor={grayScale.white} />
            ) : (
              <EyeIcon strokeColor={grayScale.white} />
            )}
          </button>
        )}
      </S.InputWrapper>
      {formState.errors.password && (
        <S.ErrorMessage name="s">{errorMessage}</S.ErrorMessage>
      )}

      {!hideIndicator && (
        <S.GuideWrapper>
          <S.GuidePhrase name="s">
            Your password should have at least:
          </S.GuidePhrase>
          <S.Characters>
            <S.Character>{MIN_PASSWORD_LENGTH} characters</S.Character>
            <S.Indicator
              name="s"
              $isError={
                !!passwordValue?.length &&
                passwordValue?.length < MIN_PASSWORD_LENGTH
              }
            >
              {passwordValue?.length || 0} / {MIN_PASSWORD_LENGTH}
            </S.Indicator>
          </S.Characters>
        </S.GuideWrapper>
      )}
    </S.Container>
  );
};

export default FormInputForPassword;
